export type BugReportSource = 'audit' | 'user';

/** Legacy severity bucket — kept for older Firestore docs. */
export type BugReportSeverity = 'CRITICAL' | 'STANDARD' | 'SUGGESTION';

/** R-82 audit-facing severity (block / warn / info). */
export type BugSeverity = 'block' | 'warn' | 'info';

export type BugCategory =
  | 'incorrect-fact'
  | 'missing-citation'
  | 'hallucination'
  | 'formatting'
  | 'ui'
  | 'other';

export type BugReportStatus =
  | 'NEW'
  | 'ACKNOWLEDGED'
  | 'IN_PROGRESS'
  | 'RESOLVED'
  | 'WONT_FIX';

/** R-131-SUP-E — triage SLA tier (default derived from severity on create). */
export type BugSlaTier = 'urgent' | 'high' | 'normal' | 'low';

/** Alias — same union as BugReportStatus for triage workflows. */
export type BugStatus = BugReportStatus;

export type BugTriageAction = 'mark_fixed' | 'wontfix' | 'duplicate';

export interface BugContext {
  url?: string;
  siteId?: string;
  reportId?: string;
  reportType?: string;
  browser?: string;
  findingId?: string;
  source?: string;
  currentRoute?: string;
}

/**
 * R-131-ATTACH — a screenshot / file attached to a bug report. `url` is a
 * token-gated media URL served by the server (no Firebase Auth required to
 * read — the token IS the credential), so admins can open it from the triage
 * panel. The object lives under the reporter's own
 * `users/{uid}/bug-attachments/…` storage prefix.
 */
export interface BugAttachment {
  url: string;
  name: string;
  contentType: string;
  /** Bytes — used for the size guard and the admin display. */
  size: number;
}

/**
 * R-131-OVERVIEW-UX-C / B1 — submit-gate minimum description length, shared by
 * the client modal (live "N more characters" hint + disabled Submit) AND the
 * server router (`description must be 15–5000 characters`). Defined ONCE here
 * so the two gates can never drift: a value the client accepts is exactly the
 * value the server accepts. Lowered 20 → 15 (the UX work intended a lower bar:
 * "The button is broken." = 22 chars passes; trivial spam like "bad" is still
 * filtered).
 */
export const BUG_DESCRIPTION_MIN_LEN = 15;

/** R-131-ATTACH — server-enforced caps. Client mirrors these in the modal. */
export const BUG_ATTACHMENT_MAX_COUNT = 5;
export const BUG_ATTACHMENT_MAX_BYTES = 10 * 1024 * 1024; // 10 MB / file
/** Allowed attachment MIME prefixes/types — images + PDF. */
export const BUG_ATTACHMENT_ALLOWED_TYPES = new Set<string>([
  'image/png',
  'image/jpeg',
  'image/jpg',
  'image/webp',
  'image/gif',
  'application/pdf',
]);

export interface BugReport {
  bugReportId: string;
  source: BugReportSource;
  title: string;
  /** @pii free-text */ // R-131-COMP-D
  description: string;
  severity: BugReportSeverity;
  submittedBy: { uid: string; /** @pii email */ email?: string };
  reporterName?: string;
  submittedAt: string;
  createdAt?: string;
  context: BugContext;
  status: BugReportStatus;
  labels: string[];
  category?: BugCategory;
  auditSeverity?: BugSeverity;
  findingId?: string;
  triageNote?: string;
  duplicateOfId?: string;
  resolutionNote?: string;
  consoleErrors?: string[];
  /**
   * Release version the report originated from, e.g. `1.1.0` (from
   * src/lib/appVersion APP_VERSION). Legacy docs may omit it; render `unknown`.
   */
  appVersion?: string;
  /**
   * Short git SHA of the exact build the report originated from, e.g.
   * `a1b2c3d` (from src/lib/appVersion BUILD_SHA). Together with appVersion this
   * pins which release/build a bug came from. Legacy docs omit it.
   */
  buildSha?: string;
  /** @pii email */ // R-131-COMP-D — legacy duplicate of submittedBy.email
  userEmail?: string;
  updatedAt?: string;
  statusUpdatedAt?: string;
  /** R-131-SUP-E — admin who owns triage. */
  assigneeUid?: string;
  /** R-131-SUP-E — set on create; legacy docs may omit. */
  slaTier?: BugSlaTier;
  /** R-131-SUP-E — ISO deadline for the active SLA milestone. */
  dueAt?: string;
  /** R-131-SUP-E — reporter inbox: set on status change, cleared on view. */
  statusChangeUnread?: boolean;
  /** R-131-SUP-E — prevents duplicate support.sla_breached audit rows. */
  slaBreachedAt?: string;
  /** R-131-ATTACH — screenshots / files attached by the reporter. */
  attachments?: BugAttachment[];
}

export interface BugReportSubmitInput {
  source: BugReportSource;
  title: string;
  description: string;
  severity?: BugReportSeverity;
  auditSeverity?: BugSeverity;
  category?: BugCategory;
  findingId?: string;
  context: BugContext;
  consoleErrors?: string[];
  /** Release version (APP_VERSION) the client built the report on. */
  appVersion?: string;
  /** Short git SHA (BUILD_SHA) the client built the report on. */
  buildSha?: string;
  userEmail?: string;
  /** R-131-ATTACH — uploaded screenshots / files (URLs returned by upload). */
  attachments?: BugAttachment[];
  /**
   * Honeypot — must stay empty for real submissions. Bots that auto-fill the
   * hidden form field populate this; the server silently drops such requests.
   */
  website?: string;
}

export interface BugReportListFilters {
  severity?: BugSeverity;
  category?: BugCategory;
  status?: BugReportStatus;
  siteId?: string;
  dateFrom?: string;
  dateTo?: string;
}

export function auditSeverityToLegacy(sev: BugSeverity): BugReportSeverity {
  if (sev === 'block') return 'CRITICAL';
  if (sev === 'info') return 'SUGGESTION';
  return 'STANDARD';
}

export function legacySeverityToAudit(sev: BugReportSeverity): BugSeverity {
  if (sev === 'CRITICAL') return 'block';
  if (sev === 'SUGGESTION') return 'info';
  return 'warn';
}
